import { buildIndustrialCaseListPath } from '@/components/apps/pages/solution/industrial/industrialCaseRoutes';

/**
 * ### NavMenuLink
 *
 * One navigable entry of the global navigation, at any depth.
 * `label` is a translation key resolved with `t()` at render time, not display text.
 */
export interface NavMenuLink {
  /**
   * ### label
   *
   * Translation key for the visible text of the link.
   */
  label: string;

  /**
   * ### url
   *
   * Internal path (`/about`) or absolute external URL.
   */
  url: string;

  /**
   * ### target
   *
   * Present only on links that leave the site; they open in a new window
   * and render the external-link mark.
   * Absent for internal routes, which navigate in place.
   */
  target?: '_blank';
}

/**
 * ### NavMenuItem
 *
 * A primary (depth 1) navigation entry with its optional submenu.
 * When `depth2` exists the primary link points at the first submenu entry
 * on desktop and only toggles the accordion on mobile.
 */
export interface NavMenuItem {
  /**
   * ### depth1
   *
   * The primary entry shown in the navigation bar.
   */
  depth1: NavMenuLink;

  /**
   * ### depth2
   *
   * Submenu entries revealed under the primary entry.
   * Never empty when present; the first entry is the desktop landing target.
   */
  depth2?: [NavMenuLink, ...Array<NavMenuLink>];
}

/**
 * ### NAV_MENU_ITEMS
 *
 * The global navigation as declared, in display order, shared by the desktop
 * and mobile headers. Changing the site structure means editing this array
 * only; the headers render whatever is declared here, including external
 * submenu links.
 *
 * Korean labels are used as translation keys throughout, so a product name
 * that stays English on the Korean screen is written English here too.
 */
export const NAV_MENU_ITEMS: ReadonlyArray<NavMenuItem> = [
  {
    // 하위 메뉴 없이 Industrial 소개로 바로 간다. 고객사례는 별도 상위 항목이 맡는다.
    depth1: { label: 'Industrial', url: '/solution/industrial' },
  },
  {
    depth1: { label: 'Safety', url: '/solution/safety' },
    depth2: [
      { label: 'Safety 소개', url: '/solution/safety' },
      {
        label: 'OpenAPI',
        url: 'https://api-docs.deeplyinc.com/reference/quick-start',
        target: '_blank',
      },
      {
        label: 'Dataset',
        url: 'https://babba.deeplyinc.com/dataset',
        target: '_blank',
      },
    ],
  },
  {
    depth1: {
      label: '고객사례',
      url: buildIndustrialCaseListPath(),
    },
  },
  {
    depth1: { label: '회사소개', url: '/about' },
    depth2: [
      { label: '회사소개', url: '/about' },
      { label: '뉴스룸', url: '/about/news' },
      { label: '자료실', url: '/about/resources' },
    ],
  },
  {
    depth1: { label: 'FAQ', url: '/faq' },
  },
  {
    depth1: { label: '문의하기', url: '/inquiry' },
  },
];
